import * as THREE from "three";
import RAPIER from "@dimforge/rapier3d-compat";

import { SceneManager } from "../../../core/scene_manager.js";
import { PhysicsWorld } from "../../../physics/physics_world.js";
import { PhysicsTable } from "../../../entities/physics_table.js";
import { EscalatorGauge } from "./escalator_gauge.js";

export class EscalatorButton
{
  private static readonly HOUSING_WIDTH_CUBES = 0.45;
  private static readonly HOUSING_HEIGHT_CUBES = 3.9;
  private static readonly CAP_HEIGHT_CUBES = 0.7;
  private static readonly CAP_LENGTH_CUBES = 0.25;
  private static readonly CAP_DEPTH_INSET_CUBES = 0.2;
  private static readonly PRESSED_SCALE = 0.3;
  private static readonly HOUSING_COLOR = 0x6f6a7d;
  private static readonly CAP_COLOR = 0xe0302b;
  private static readonly LAMP_FRONT_INSET_CUBES = 0.05;
  private static readonly LAMP_ABOVE_HOUSING_CUBES = 0.8;
  private static readonly REACH_SLACK = 0.02;

  private readonly meshes: THREE.Mesh[] = [];
  private readonly cap: THREE.Mesh;
  private readonly gauge: EscalatorGauge;
  private readonly body: RAPIER.RigidBody;
  private readonly face_x: number;
  private readonly top_y: number;
  private readonly surface_y: number;
  private readonly lamp_spot: THREE.Vector3;

  constructor( private readonly table: PhysicsTable, cube_size: THREE.Vector3, private readonly mirrored: boolean = false )
  {
    const dir = mirrored ? -1 : 1;

    const top_center = table.getTopCenter();
    const top_size = table.getTopSize();
    const depth = top_size.z;
    const z = top_center.z;

    const housing_w = cube_size.x * EscalatorButton.HOUSING_WIDTH_CUBES;
    const housing_h = cube_size.y * EscalatorButton.HOUSING_HEIGHT_CUBES;
    const deck_edge_x = top_center.x + dir * top_size.x * 0.5;
    const housing_x = deck_edge_x - dir * housing_w * 0.5;
    const housing_face_x = deck_edge_x - dir * housing_w;
    const surface_y = table.surfaceYAt( housing_x, z );
    const housing_y = surface_y + housing_h * 0.5;

    this.body = PhysicsWorld.getWorld().createRigidBody(
      RAPIER.RigidBodyDesc.fixed().setTranslation( housing_x, housing_y, z )
    );

    PhysicsWorld.getWorld().createCollider(
      RAPIER.ColliderDesc.cuboid( housing_w * 0.5, housing_h * 0.5, depth * 0.5 ).setFriction( 1 ),
      this.body
    );

    const housing_geometry = new THREE.BoxGeometry( housing_w, housing_h, depth );
    const housing = this.addMesh( housing_geometry, EscalatorButton.HOUSING_COLOR );
    housing.position.set( housing_x, housing_y, z );

    const housing_front_z = z + depth * 0.5;
    this.gauge = new EscalatorGauge( housing_x, housing_y, housing_front_z, housing_w, housing_h );

    const cap_h = cube_size.y * EscalatorButton.CAP_HEIGHT_CUBES;
    const cap_length = cube_size.x * EscalatorButton.CAP_LENGTH_CUBES;
    const cap_depth = depth - cube_size.z * EscalatorButton.CAP_DEPTH_INSET_CUBES;
    const cap_y = surface_y + cube_size.y * 0.5;

    const cap_geometry = new THREE.BoxGeometry( cap_length, cap_h, cap_depth ).translate( -dir * cap_length * 0.5, 0, 0 );
    this.cap = this.addMesh( cap_geometry, EscalatorButton.CAP_COLOR );
    this.cap.position.set( housing_face_x, cap_y, z );

    this.face_x = housing_face_x - dir * cap_length;
    this.top_y = surface_y + housing_h;
    this.surface_y = surface_y;

    const lamp_z = housing_front_z - cube_size.z * EscalatorButton.LAMP_FRONT_INSET_CUBES;
    const lamp_top_y = this.top_y + cube_size.y * EscalatorButton.LAMP_ABOVE_HOUSING_CUBES;

    this.lamp_spot = new THREE.Vector3( housing_x, lamp_top_y, lamp_z );
  }

  public getLampSpot(): THREE.Vector3
  {
    return this.lamp_spot.clone();
  }

  public getSurfaceY(): number
  {
    return this.surface_y;
  }

  public getFaceX(): number
  {
    return this.face_x;
  }

  public isReachedBy( position: RAPIER.Vector, size: THREE.Vector3 ): boolean
  {
    if ( this.table.isOutside( position ) )
      return false;

    const bottom_y = position.y - size.y * 0.5;

    if ( bottom_y >= this.top_y )
      return false;

    if ( this.mirrored )
      return position.x - size.x * 0.5 - EscalatorButton.REACH_SLACK <= this.face_x;

    return position.x + size.x * 0.5 + EscalatorButton.REACH_SLACK >= this.face_x;
  }

  public press(): void
  {
    this.cap.scale.x = EscalatorButton.PRESSED_SCALE;
  }

  public release(): void
  {
    this.cap.scale.x = 1;
  }

  public setProgress( value: number ): void
  {
    this.gauge.setProgress( value );
  }

  public dispose(): void
  {
    this.gauge.dispose();

    for ( const mesh of this.meshes )
    {
      SceneManager.world.remove( mesh );
      mesh.geometry.dispose();
      ( mesh.material as THREE.Material ).dispose();
    }

    this.meshes.length = 0;
    PhysicsWorld.getWorld().removeRigidBody( this.body );
  }

  private addMesh( geometry: THREE.BufferGeometry, color: number ): THREE.Mesh
  {
    const mesh = new THREE.Mesh( geometry, new THREE.MeshStandardMaterial( { color } ) );

    mesh.castShadow = true;
    mesh.receiveShadow = true;
    SceneManager.world.add( mesh );
    this.meshes.push( mesh );

    return mesh;
  }
}
